import { TRACK_DEFAULT_MAP_STYLE, TRACK_MAP_THEMES } from './track.const';

// ── Track map view preferences (base style / projection / timezone overlay) ────
// These three used to live in `useState` only and reset on every page load. They
// now persist per user in localStorage, read synchronously during useState init
// so the map boots in the saved view with no flash of the default one.
//
// Ported from wal-frontend-v2 src/app/Radar/radar.preferences.ts with ONE
// deliberate difference: WAL also mirrors the bag to the backend
// (users.radarSettings.mapView) so it follows the user across browsers. The
// portal keeps it in localStorage ONLY — no backend copy, no update-user call —
// by product decision (2026-09-09). So a cleared cache or a new browser starts
// from the defaults again.
//
// The key is namespaced by user id: on a shared computer, user B must never see
// user A's cached view. Without a user id nothing is read or written.

type TrackMapProjection = 'mercator' | 'globe';

interface ITrackMapPreferences {
    mapStyle: string;
    projection: TrackMapProjection;
    showTimezones: boolean;
}

const TRACK_MAP_PREFERENCE_DEFAULTS: ITrackMapPreferences = {
    mapStyle: TRACK_DEFAULT_MAP_STYLE,
    projection: 'mercator',
    showTimezones: false,
};

const storageKey = (userId?: number | string | null): string | null => (userId ? `track_map_prefs_${userId}` : null);

// Accept only values we know are safe to hand to Mapbox. A stale style URL left in
// localStorage from a retired theme (or an edited entry) would otherwise reach
// map.setStyle() and fail to load, leaving a blank map.
const sanitize = (raw: unknown): Partial<ITrackMapPreferences> => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

    const value = raw as Record<string, unknown>;
    const result: Partial<ITrackMapPreferences> = {};

    if (typeof value.mapStyle === 'string' && TRACK_MAP_THEMES.some((theme) => theme.value === value.mapStyle)) {
        result.mapStyle = value.mapStyle;
    }

    if (value.projection === 'mercator' || value.projection === 'globe') {
        result.projection = value.projection;
    }

    if (typeof value.showTimezones === 'boolean') {
        result.showTimezones = value.showTimezones;
    }

    return result;
};

// Every access is wrapped: localStorage throws in private-mode/blocked-cookie
// browsers, and a preference read must never take the Track page down with it.
export const readTrackMapPreferences = (userId?: number | string | null): ITrackMapPreferences => {
    const key = storageKey(userId);

    if (!key) return { ...TRACK_MAP_PREFERENCE_DEFAULTS };

    try {
        const stored = window.localStorage.getItem(key);

        if (!stored) return { ...TRACK_MAP_PREFERENCE_DEFAULTS };

        return { ...TRACK_MAP_PREFERENCE_DEFAULTS, ...sanitize(JSON.parse(stored)) };
    } catch {
        return { ...TRACK_MAP_PREFERENCE_DEFAULTS };
    }
};

export const writeTrackMapPreferences = (userId: number | string | null | undefined, patch: Partial<ITrackMapPreferences>): void => {
    const key = storageKey(userId);

    if (!key) return;

    try {
        const next = { ...readTrackMapPreferences(userId), ...sanitize(patch) };
        window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
        // Storage full or unavailable — the view still applies for this session.
    }
};

// ── Track sidebar section collapse state ──────────────────────────────────────
// Which collapsible sidebar cards the user left open (today: the Overview stats
// card). Same localStorage-only, per-user-id treatment as the map view above,
// under its own key so a map-view write can never clobber it.
//
// Read synchronously into CardCmp's `defaultOpen` so a section the user had
// collapsed never flashes open before being closed again.
//
// Ported from the sidebar-section half of WAL's radar.preferences.ts.

type TrackSectionKey = 'overview';

// Matches what each card shipped with before this was persisted — a user who has
// never toggled a section must see exactly the layout they saw yesterday.
const TRACK_SECTION_DEFAULT_OPEN: Record<TrackSectionKey, boolean> = {
    overview: false,
};

const sectionStorageKey = (userId?: number | string | null): string | null => (userId ? `track_sections_${userId}` : null);

export const readTrackSectionOpen = (userId: number | string | null | undefined, section: TrackSectionKey): boolean => {
    const key = sectionStorageKey(userId);

    if (!key) return TRACK_SECTION_DEFAULT_OPEN[section];

    try {
        const stored = window.localStorage.getItem(key);

        if (!stored) return TRACK_SECTION_DEFAULT_OPEN[section];

        const parsed = JSON.parse(stored);

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return TRACK_SECTION_DEFAULT_OPEN[section];

        const value = (parsed as Record<string, unknown>)[section];

        return typeof value === 'boolean' ? value : TRACK_SECTION_DEFAULT_OPEN[section];
    } catch {
        return TRACK_SECTION_DEFAULT_OPEN[section];
    }
};

export const writeTrackSectionOpen = (userId: number | string | null | undefined, section: TrackSectionKey, isOpen: boolean): void => {
    const key = sectionStorageKey(userId);

    if (!key) return;

    try {
        const stored = window.localStorage.getItem(key);
        const parsed = stored ? JSON.parse(stored) : null;
        const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};

        window.localStorage.setItem(key, JSON.stringify({ ...base, [section]: isOpen }));
    } catch {
        // Storage full or unavailable — the section still toggles for this session.
    }
};

// ── Track active saved view ────────────────────────────────────────────────────
// Which saved view (custom_views id) the user last selected on the Track page, so a
// reload lands back on it instead of the Pickups stage. Only a view id is stored —
// the stage tabs are the default and need no memory. Validated against the fetched
// list on restore (a deleted view falls back to the default), see useTrackViews.
// Ported from WAL's RADAR_ACTIVE_VIEW_STORAGE_KEY, namespaced per user like the rest.

const activeViewStorageKey = (userId?: number | string | null): string | null => (userId ? `track_active_view_${userId}` : null);

export const readTrackActiveView = (userId?: number | string | null): string | null => {
    const key = activeViewStorageKey(userId);

    if (!key) return null;

    try {
        const stored = window.localStorage.getItem(key);

        return stored && stored.trim() ? stored : null;
    } catch {
        return null;
    }
};

// Pass null when a stage tab becomes active so the memory is cleared, not left stale.
export const writeTrackActiveView = (userId: number | string | null | undefined, viewValue: string | null): void => {
    const key = activeViewStorageKey(userId);

    if (!key) return;

    try {
        if (viewValue) {
            window.localStorage.setItem(key, viewValue);
        } else {
            window.localStorage.removeItem(key);
        }
    } catch {
        // Storage unavailable — the view still applies for this session.
    }
};

// ── Track map / table view mode ───────────────────────────────────────────────
// Which of the two list surfaces the user last chose (Map View vs Table View), so a
// reload lands back on it instead of always booting into the map. WAL keeps this in its
// persisted params bag (`mapOrTableView`); the portal can't — trackParams is the API
// payload and a UI-only key doesn't belong in it — so it rides in localStorage beside
// the other Track preferences, per user id, same as the map style above.

export type TrackViewMode = 'map_view' | 'table_view';

const TRACK_DEFAULT_VIEW_MODE: TrackViewMode = 'map_view';

const viewModeStorageKey = (userId?: number | string | null): string | null => (userId ? `track_view_mode_${userId}` : null);

export const readTrackViewMode = (userId?: number | string | null): TrackViewMode => {
    const key = viewModeStorageKey(userId);

    if (!key) return TRACK_DEFAULT_VIEW_MODE;

    try {
        const stored = window.localStorage.getItem(key);

        // Anything other than the two known modes (a retired value, a hand-edited entry)
        // falls back to the default rather than rendering neither surface.
        return stored === 'map_view' || stored === 'table_view' ? stored : TRACK_DEFAULT_VIEW_MODE;
    } catch {
        return TRACK_DEFAULT_VIEW_MODE;
    }
};

export const writeTrackViewMode = (userId: number | string | null | undefined, mode: TrackViewMode): void => {
    const key = viewModeStorageKey(userId);

    if (!key) return;

    try {
        window.localStorage.setItem(key, mode);
    } catch {
        // Storage full or unavailable — the choice still applies for this session.
    }
};

// ── Track working filter set ──────────────────────────────────────────────────
// The ad-hoc filters the user has applied but not saved as a view (stage tab, service type,
// division, billing account, search, ref, shipper/consignee, origin/destination, date range,
// sub-status drill-down, sort, page size). Kept so a refresh lands back on what the user was
// looking at instead of resetting to Pickups/Today.
//
// sessionStorage, not localStorage: a refresh (or a trip into an order and back) keeps the
// filters, while a brand-new tab or a fresh browser session starts clean — the same treatment
// WAL gives it through useStoredViewState. A saved view still wins on restore; this only covers
// the un-saved state.
//
// `page` is deliberately NOT persisted: the row it pointed at may be gone by the next load, and
// landing on an empty page 4 reads as "my data disappeared".

const TRACK_FILTER_STATE_KEYS = [
    'status',
    'serviceType',
    'sortField',
    'sortType',
    'startDate',
    'endDate',
    'billingAccountId',
    'divisionId',
    'search',
    'refSearch',
    'shipperId',
    'consigneeId',
    'originSearch',
    'destinationSearch',
    'filterBySubStatus',
    'limit',
] as const;

interface ITrackFilterState {
    params: Record<string, unknown>;
    activeViewTab: string | null;
}

const filterStateStorageKey = (userId?: number | string | null): string | null => (userId ? `track_filter_state_${userId}` : null);

export const readTrackFilterState = (userId?: number | string | null): ITrackFilterState | null => {
    const key = filterStateStorageKey(userId);

    if (!key) return null;

    try {
        const stored = window.sessionStorage.getItem(key);

        if (!stored) return null;

        const parsed = JSON.parse(stored);

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

        // Only keys this module owns are handed back — anything else in a hand-edited or
        // stale entry must never reach setTrackParams and become part of the API payload.
        const params: Record<string, unknown> = {};
        TRACK_FILTER_STATE_KEYS.forEach((field) => {
            if (parsed?.params && field in parsed.params) params[field] = parsed.params[field];
        });

        if (Object.keys(params).length === 0) return null;

        return { params, activeViewTab: typeof parsed.activeViewTab === 'string' ? parsed.activeViewTab : null };
    } catch {
        return null;
    }
};

export const writeTrackFilterState = (userId: number | string | null | undefined, params: Record<string, unknown>, activeViewTab: string | null): void => {
    const key = filterStateStorageKey(userId);

    if (!key) return;

    try {
        const picked: Record<string, unknown> = {};
        TRACK_FILTER_STATE_KEYS.forEach((field) => {
            if (field in params) picked[field] = params[field];
        });

        window.sessionStorage.setItem(key, JSON.stringify({ params: picked, activeViewTab }));
    } catch {
        // Storage full or unavailable — filters still apply for this session.
    }
};
